import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { Box, FormHelperText } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Autocomplete, DatePickerWithInput, Switch, TimePickerWithInput, TimeZoneFormattedUtils } from '@utriper/core-ui';
import { Container, Label } from '../../styles';

const WhenTab = () => {
    const { t } = useTranslation();
    const translate = (key, options) => t(`experience_calendar.schedule_form.when_tab.${key}`, options);
    const { values } = useFormState();

    const duration = useMemo(() => {
        if (!values?.start?.isValid || !values?.startDuration?.isValid || !values?.endDuration?.isValid) {
            return null;
        }

        const startDate = values.start.set({
            hour: values.startDuration.hour,
            minute: values.startDuration.minute,
            second: 0,
            millisecond: 0,
        });

        const endBaseDate = values?.hasEndDate && values?.end?.isValid ? values.end : values.start;
        const endDate = endBaseDate.set({
            hour: values.endDuration.hour,
            minute: values.endDuration.minute,
            second: 0,
            millisecond: 0,
        });

        return endDate.diff(startDate, 'hours').hours;
    }, [values]);

    const timezoneOptions = Intl.supportedValuesOf('timeZone').map((timezone) => ({
        id: timezone,
        label: TimeZoneFormattedUtils(timezone),
    }));

    return (
        <React.Fragment>
            {/* Start Date */}
            <Container>
                <Label>{translate('start_date_label')}</Label>
                <Field
                    id="start"
                    name="start"
                    render={({ input }) => (
                        <DatePickerWithInput
                            {...input}
                            placeholder={translate('start_date_placeholder')}
                            style={{
                                width: '100%',
                            }}
                            useUTC
                        />
                    )}
                />
            </Container>

            {/* Duration */}
            <Container>
                <Label>{translate('duration_label')}</Label>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Field id="startDuration" name="startDuration" label={translate('start_duration_label')} component={TimePickerWithInput} useUTC />
                    <ArrowForward />
                    <Field id="endDuration" name="endDuration" label={translate('end_duration_label')} component={TimePickerWithInput} useUTC />
                </Box>
                {duration && duration > 0 && <FormHelperText>{translate('duration_helper', { duration })}</FormHelperText>}
            </Container>

            {/* End Date Switch */}
            <Container>
                <Field id="hasEndDate" name="hasEndDate" type="checkbox" component={Switch} label={translate('has_end_date_label')} />
            </Container>

            {/* End Date */}
            {values?.hasEndDate && (
                <Container>
                    <Label>{translate('end_date_label')}</Label>
                    <Field
                        id="end"
                        name="end"
                        render={({ input }) => (
                            <DatePickerWithInput
                                {...input}
                                placeholder={translate('end_date_placeholder')}
                                style={{
                                    width: '100%',
                                }}
                                useUTC
                            />
                        )}
                    />
                </Container>
            )}

            {/* Timezone */}
            <Container>
                <Label>{translate('timezone_label')}</Label>
                <Field id="timezone" name="timezone" options={timezoneOptions} component={Autocomplete} label={translate('timezone_placeholder')} fullWidth valueAsId />
            </Container>

            {/* Time Period */}
            <Container>
                <Field id="timePeriod" name="timePeriod" type="checkbox" component={Switch} label={translate('time_period_label')} disabled={true} />
                <FormHelperText>{translate('time_period_helper')}</FormHelperText>
            </Container>
        </React.Fragment>
    );
};

export default WhenTab;
